import { spansOverlap } from '../geom'
import type { Platform } from '../track/types'
import { nearBody } from '../track/xindex'
import { NO_TICK } from '../world'
import type { PlatformState, PlayerState, World } from '../world'

/** Triangle wave in `[0, 1]`: 0 at phase 0, 1 at half period, back to 0 (D-020, no trig). */
function triangle(tick: number, periodTicks: number): number {
  const phase = tick % periodTicks
  const half = periodTicks / 2
  return phase < half ? phase / half : (periodTicks - phase) / half
}

/** Position of a platform at `tick`. Crumbling platforms never move. */
export function platformPosAt(pl: Platform, tick: number): { x: number; y: number } {
  if (pl.kind !== 'moving') return { x: pl.x, y: pl.y }
  const f = triangle(tick, pl.periodTicks)
  return { x: pl.x + pl.dx * f, y: pl.y + pl.dy * f }
}

export function isPlatformSolid(st: PlatformState, tick: number): boolean {
  return st.fallAt === NO_TICK || tick < st.fallAt || tick >= st.backAt
}

/**
 * Index of the platform `p` is standing on at the start of the step, or -1. Standing means
 * grounded with feet exactly on the platform's current top (landing snaps feet to it).
 */
export function findRiddenPlatform(world: World, p: PlayerState): number {
  if (!p.grounded) return -1
  const { platforms, index } = world.track
  for (const k of nearBody(index.platforms, p)) {
    const pl = platforms[k]!
    const st = world.platformState[k]!
    if (p.y === st.y && spansOverlap(p.x, p.w, st.x, pl.w) && isPlatformSolid(st, world.tick)) {
      return k
    }
  }
  return -1
}

/** Movement of each platform this step, filled by `advancePlatforms`. */
export interface PlatformDelta {
  prevX: number
  prevY: number
  dx: number
  dy: number
}

/**
 * Move platforms to their position for `tick + 1` (the state this step produces) and restore
 * crumbled platforms whose respawn tick has come. Returns where each one was and how far it moved.
 */
export function advancePlatforms(world: World): PlatformDelta[] {
  const { platforms } = world.track
  const deltas: PlatformDelta[] = []
  for (let k = 0; k < platforms.length; k++) {
    const pl = platforms[k]!
    const st = world.platformState[k]!
    const prevX = st.x
    const prevY = st.y
    const next = platformPosAt(pl, world.tick + 1)
    st.x = next.x
    st.y = next.y
    if (st.backAt !== NO_TICK && world.tick >= st.backAt) {
      st.fallAt = NO_TICK
      st.backAt = NO_TICK
    }
    deltas.push({ prevX, prevY, dx: next.x - prevX, dy: next.y - prevY })
  }
  return deltas
}

/** Start the crumble timer the first time a player stands on a crumbling platform. */
export function touchPlatform(world: World, k: number): void {
  const pl = world.track.platforms[k]!
  const st = world.platformState[k]!
  if (pl.kind !== 'crumbling' || st.fallAt !== NO_TICK) return
  st.fallAt = world.tick + world.cfg.crumbleDelayTicks
  st.backAt = st.fallAt + world.cfg.crumbleRespawnTicks
}

/**
 * One-way landing: falling, feet at or above the platform top at the start of the step, at or
 * below it now. Returns the platform index landed on (highest top wins) or -1.
 */
export function landOnPlatform(
  world: World,
  p: PlayerState,
  prevFoot: number,
  deltas: readonly PlatformDelta[],
): number {
  if (p.vy < 0) return -1
  const { platforms, index } = world.track
  let best = -1
  let bestTop = 0
  for (const k of nearBody(index.platforms, p)) {
    const pl = platforms[k]!
    const st = world.platformState[k]!
    const d = deltas[k]!
    if (!isPlatformSolid(st, world.tick)) continue
    if (prevFoot > d.prevY || p.y < st.y || !spansOverlap(p.x, p.w, st.x, pl.w)) continue
    if (best === -1 || st.y < bestTop) {
      best = k
      bestTop = st.y
    }
  }
  if (best !== -1) p.y = bestTop
  return best
}
